import { useEnv } from '@sparklayer-io/spark-dash-core/context/env-context';
import { trackUpgradeClicked } from '@sparklayer-io/spark-dash-core/lib/mixpanel';
import {
  QuotingGraphic,
  SalesAgentsGraphic,
} from '@sparklayer-io/spark-dash-pages/components/addon-graphics';
import {
  PopularAddons,
  type PopularAddonsItem,
} from '@sparklayer-io/spark-dash-pages/components/popular-addons';
import { useHandlers, useSite } from '@sparklayer-io/spark-dash-pages/context/page-context';
import { Button } from '@ui/themed';
import { t } from 'i18next';
import type { I18nPrefix } from '../../../i18n/prefix';
import { ApiAccessGraphic, CompanyUsersGraphic, InvoicesGraphic } from './upsell-plan-graphics';

const COMPONENT = 'features:home.components.upsell_plan' satisfies I18nPrefix;

export function UpsellPlan() {
  const env = useEnv();
  const site = useSite();
  const { navigationHandler } = useHandlers();
  const plan = env.billing.plan_key;

  const cta = (needsUpgrade: boolean, title: string, action: string) =>
    needsUpgrade ? (
      <Button
        variant="secondary"
        icon="upgrade"
        onClick={() => {
          trackUpgradeClicked(site, title);
          navigationHandler.navigateById('plan');
        }}
      >
        {t('buttons:upgrade')}
      </Button>
    ) : (
      <Button
        variant="secondary"
        icon="settings"
        onClick={() => navigationHandler.navigate(action)}
      >
        {t('buttons:enable')}
      </Button>
    );

  const items: PopularAddonsItem[] = [
    {
      title: t(`${COMPONENT}.sales_agents.title`),
      illustration: <SalesAgentsGraphic />,
      description: t(`${COMPONENT}.sales_agents.description`),
      link: 'https://docs.sparklayer.io/sales-agent-ordering',
      plan: 'v2_basic',
      cta: cta(plan === 'basic_free', t(`${COMPONENT}.sales_agents.title`), '/customers/agents'),
    },
    {
      title: t(`${COMPONENT}.invoices.title`),
      illustration: <InvoicesGraphic />,
      description: t(`${COMPONENT}.invoices.description`),
      link: 'https://docs.sparklayer.io/invoices',
      plan: 'v2_growth',
      cta: cta(plan === 'v2_basic', t(`${COMPONENT}.invoices.title`), '/configuration/general'),
    },
    {
      title: t(`${COMPONENT}.quoting.title`),
      illustration: <QuotingGraphic />,
      description: t(`${COMPONENT}.quoting.description`),
      link: 'https://docs.sparklayer.io/quoting',
      plan: 'v2_pro',
      cta: cta(plan === 'v2_basic', t(`${COMPONENT}.quoting.title`), '/configuration/general'),
    },
    {
      title: t(`${COMPONENT}.company_users.title`),
      illustration: <CompanyUsersGraphic />,
      description: t(`${COMPONENT}.company_users.description`),
      link: 'https://docs.sparklayer.io/company-users',
      plan: 'v2_pro',
      cta: cta(
        plan === 'v2_basic' || plan === 'v2_growth',
        t(`${COMPONENT}.company_users.title`),
        '/configuration/general',
      ),
      className: 'hidden 2xl:block',
    },
    {
      title: t(`${COMPONENT}.api_access.title`),
      illustration: <ApiAccessGraphic />,
      description: t(`${COMPONENT}.api_access.description`),
      link: 'https://docs.sparklayer.io/sparklayer-api',
      plan: 'v2_pro',
      cta: cta(
        plan === 'v2_basic' || plan === 'v2_growth',
        t(`${COMPONENT}.api_access.title`),
        '/configuration/api',
      ),
    },
  ];

  return <PopularAddons items={items} />;
}
